import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Activity, Download, Users, TrendingUp, Clock, ChevronDown, AlertTriangle, Mail, Send, Eye, CheckCircle2, XCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";

const TEMPLATE_COLORS = ["#EF4444", "#0EA5E9", "#16A34A", "#7C3AED", "#F59E0B", "#1E40AF", "#3F3F46", "#DC2626"];

function ProEditionBlastPanel() {
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null); // { sent, recipients, ... }
  const [result, setResult] = useState(null);

  const runDryRun = async () => {
    setPreviewing(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/run-pro-edition-blast?dry_run=true");
      setPreview(data);
      toast.success(`Dry-run: ${data.sent} recipients queued (nothing sent yet)`);
    } catch (e) {
      toast.error("Dry-run failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setPreviewing(false);
    }
  };

  const sendReal = async () => {
    if (!preview || preview.sent === 0) {
      toast.error("Run dry-run first to preview recipients.");
      return;
    }
    const ok = window.confirm(
      `Send Pro Edition upgrade email to ${preview.sent} pack owner(s)?\n\nThis cannot be undone (idempotency prevents duplicates on re-run).`
    );
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/run-pro-edition-blast?dry_run=false");
      setResult(data);
      toast.success(`Blast complete: ${data.sent} sent, ${data.failed} failed`);
      setPreview(null); // Clear preview so button disables until next dry-run
    } catch (e) {
      toast.error("Blast failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 p-6 mb-10" data-testid="pro-edition-blast-panel">
      <div className="flex items-start gap-4">
        <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white shrink-0">
          <Mail className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="overline mb-1 text-indigo-600">◇ EMAIL BLAST · ONE-TIME</div>
          <h2 className="font-display font-bold text-xl text-zinc-900 mb-1">Pro Edition Upgrade Announcement</h2>
          <p className="text-sm text-zinc-600 max-w-2xl">
            Sends a one-time email to every pack owner announcing the 12+ new questions per pack (Pro Edition upgrade at no extra cost). Idempotent — users already notified are skipped automatically.
          </p>

          <div className="mt-5 flex flex-col sm:flex-row items-start gap-3">
            <button
              onClick={runDryRun}
              disabled={previewing || sending}
              data-testid="admin-blast-dry-run"
              className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 hover:bg-zinc-900 hover:text-white text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              {previewing ? "Previewing…" : "1. Dry-run preview"}
            </button>

            <button
              onClick={sendReal}
              disabled={!preview || preview.sent === 0 || sending || previewing}
              data-testid="admin-blast-send"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:opacity-90 text-white text-sm font-medium disabled:opacity-40 disabled:from-zinc-400 disabled:to-zinc-400 transition-opacity"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : preview && preview.sent > 0 ? `2. Send to ${preview.sent} recipient${preview.sent === 1 ? "" : "s"}` : "2. Send (run dry-run first)"}
            </button>
          </div>

          {preview && !result ? (
            <div className="mt-5 border border-zinc-200 bg-zinc-50 p-4" data-testid="admin-blast-preview">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-zinc-500" />
                <span className="font-mono uppercase tracking-wider text-[11px] text-zinc-500">Dry-run result</span>
              </div>
              <div className="text-sm text-zinc-800">
                <strong className="font-display text-lg">{preview.sent}</strong> recipient{preview.sent === 1 ? "" : "s"} will receive the email.
              </div>
              {preview.recipients && preview.recipients.length > 0 ? (
                <details className="mt-3">
                  <summary className="text-xs font-mono uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-900">
                    Show recipient list ({preview.recipients.length})
                  </summary>
                  <ul className="mt-2 max-h-40 overflow-auto text-xs text-zinc-700 space-y-0.5 font-mono">
                    {preview.recipients.map((r) => (
                      <li key={r} className="truncate">{r}</li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ) : null}

          {result ? (
            <div className={`mt-5 border p-4 ${result.failed > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="admin-blast-result">
              <div className="flex items-center gap-2 mb-2">
                {result.failed > 0 ? <XCircle className="w-4 h-4 text-amber-600" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                <span className="font-mono uppercase tracking-wider text-[11px] text-zinc-700">Blast complete</span>
              </div>
              <div className="text-sm text-zinc-800 flex flex-wrap gap-x-6 gap-y-1">
                <span><strong>{result.sent}</strong> sent</span>
                <span><strong>{result.failed}</strong> failed</span>
                <span><strong>{result.skipped}</strong> skipped</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub, icon: Icon, testId }) {
  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest mb-2">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="font-display font-extrabold text-3xl text-zinc-900 tabular-nums">{value}</div>
      {sub ? <div className="text-xs text-zinc-500 mt-1">{sub}</div> : null}
    </div>
  );
}

function fmtSeconds(s) {
  if (!s) return "—";
  if (s < 60) return `${s.toFixed(0)}s`;
  if (s < 3600) return `${(s / 60).toFixed(1)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

export default function AdminBuilder() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return; // ProtectedRoute will redirect to /login
    if (!user.is_admin) {
      setError("Admin access required. Your account email is not in the ADMIN_EMAILS allowlist.");
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/builder/funnel?days=${days}`);
        setData(data);
      } catch (e) {
        toast.error("Failed to load analytics", { description: e?.response?.data?.detail || e.message });
        setError(e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, user, days, navigate]);

  if (authLoading || loading) {
    return (
      <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 flex items-center justify-center" data-testid="admin-builder-loading">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 flex items-center justify-center px-6" data-testid="admin-builder-error">
        <div className="bg-white border border-amber-300 p-6 max-w-md text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <h2 className="font-display font-bold text-xl mb-2">Access denied</h2>
          <p className="text-sm text-zinc-600">{error}</p>
          <Link to="/" className="inline-block mt-4 text-sm font-semibold text-[#EF4444] hover:underline">← Back to home</Link>
        </div>
      </main>
    );
  }

  const t = data?.totals || {};
  const funnel = data?.funnel || [];
  const distribution = data?.template_distribution || [];
  const daily = data?.daily || [];
  const recent = data?.recent || [];

  return (
    <>
      <SEO title="Builder Analytics · Admin" description="Internal builder funnel analytics dashboard." noindex />
      <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50" data-testid="admin-builder-page">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8">
            <div>
              <div className="overline mb-2">◆ ADMIN · BUILDER FUNNEL</div>
              <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-zinc-900">
                Resume Builder Analytics
              </h1>
              <p className="text-zinc-600 text-sm mt-2">
                Last {data?.window_days || days} days · {t.total_events || 0} events tracked · Signed in as <strong>{user.email}</strong>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-zinc-500 uppercase tracking-widest">Window</label>
              <div className="relative">
                <select
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  data-testid="admin-window-select"
                  className="appearance-none border border-zinc-300 bg-white pl-3 pr-8 py-1.5 text-sm font-mono"
                >
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={365}>1 year</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Pro Edition Blast Panel — one-time upgrade announcement */}
          <ProEditionBlastPanel />

          {/* PANEL 1 — Totals */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-totals">
            <MetricCard label="Sessions started" value={t.sessions ?? 0} icon={Users} testId="metric-sessions" />
            <MetricCard label="Downloads" value={t.downloads ?? 0} icon={Download} testId="metric-downloads" />
            <MetricCard label="Conversion" value={`${t.conversion_pct ?? 0}%`} sub={`${t.sessions ?? 0} sessions → ${t.downloads ?? 0} dl`} icon={TrendingUp} testId="metric-conversion" />
            <MetricCard label="Avg time to download" value={fmtSeconds(t.avg_seconds_to_download)} sub={`${t.unique_users ?? 0} unique users`} icon={Clock} testId="metric-time" />
          </div>

          {/* PANEL 2 — Funnel chart */}
          <section className="bg-white border border-zinc-200 p-6 mb-8" data-testid="admin-funnel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg text-zinc-900">Step Funnel</h2>
              <span className="text-xs text-zinc-500 font-mono">distinct sessions reaching each step</span>
            </div>
            {funnel.every((s) => s.sessions === 0) ? (
              <p className="text-sm text-zinc-500 py-8 text-center">No funnel data yet. Get your first builder users in to see step-by-step drop-off.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={funnel} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#f4f4f5" />
                  <XAxis dataKey="step" tick={{ fontSize: 11, fill: "#52525b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#52525b" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", fontSize: 12 }}
                    formatter={(v, k, p) => [v, "sessions"]}
                    labelFormatter={(label) => {
                      const item = funnel.find((f) => f.step === label);
                      return `${label}${item && item.drop_pct ? ` · −${item.drop_pct}% drop` : ""}`;
                    }}
                  />
                  <Bar dataKey="sessions" fill="#EF4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              {funnel.map((s, i) => (
                <div key={s.step} className="flex items-center gap-1.5 px-2 py-1 bg-zinc-50 border border-zinc-200 font-mono">
                  <span className="text-zinc-500">{i + 1}.</span>
                  <span className="font-semibold text-zinc-900">{s.step}</span>
                  <span className="text-zinc-500">→</span>
                  <span className="text-zinc-900 tabular-nums">{s.sessions}</span>
                  {s.drop_pct > 0 ? <span className="text-red-600 ml-1 tabular-nums">−{s.drop_pct}%</span> : null}
                </div>
              ))}
            </div>
          </section>

          {/* PANEL 3 + 4 — Template distribution + Daily activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <section className="bg-white border border-zinc-200 p-6" data-testid="admin-templates">
              <h2 className="font-display font-bold text-lg text-zinc-900 mb-4">Template Distribution</h2>
              {distribution.length === 0 ? (
                <p className="text-sm text-zinc-500 py-8 text-center">No downloads tracked yet.</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={distribution} dataKey="count" nameKey="template" innerRadius={50} outerRadius={90} paddingAngle={2}>
                        {distribution.map((entry, idx) => (
                          <Cell key={entry.template} fill={TEMPLATE_COLORS[idx % TEMPLATE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", fontSize: 12 }}
                        formatter={(v, n, p) => [`${v} (${p.payload.pct}%)`, p.payload.template]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5 mt-3 text-xs">
                    {distribution.map((d, i) => (
                      <div key={d.template} className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ background: TEMPLATE_COLORS[i % TEMPLATE_COLORS.length] }} />
                        <span className="font-mono text-zinc-700 flex-1">{d.template}</span>
                        <span className="font-semibold text-zinc-900 tabular-nums">{d.count}</span>
                        <span className="text-zinc-500 tabular-nums w-10 text-right">{d.pct}%</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>

            <section className="bg-white border border-zinc-200 p-6" data-testid="admin-daily">
              <h2 className="font-display font-bold text-lg text-zinc-900 mb-4">Daily Activity</h2>
              {daily.every((d) => d.sessions === 0 && d.downloads === 0) ? (
                <p className="text-sm text-zinc-500 py-8 text-center">No activity in the selected window.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={daily} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#f4f4f5" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "#52525b" }}
                      tickFormatter={(d) => d.slice(5)}
                      interval={Math.max(1, Math.floor(daily.length / 8))}
                    />
                    <YAxis tick={{ fontSize: 11, fill: "#52525b" }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", fontSize: 12 }}
                      labelFormatter={(d) => `📅 ${d}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="sessions" stroke="#0EA5E9" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="downloads" stroke="#16A34A" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </section>
          </div>

          {/* PANEL 5 — Recent events */}
          <section className="bg-white border border-zinc-200 p-6" data-testid="admin-recent">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg text-zinc-900">Recent Events</h2>
              <span className="text-xs text-zinc-500 font-mono">{recent.length} most recent</span>
            </div>
            {recent.length === 0 ? (
              <p className="text-sm text-zinc-500 py-8 text-center">No events recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left border-b border-zinc-200">
                      <th className="py-2 pr-4 font-semibold text-zinc-500 uppercase tracking-widest">Timestamp</th>
                      <th className="py-2 pr-4 font-semibold text-zinc-500 uppercase tracking-widest">Event</th>
                      <th className="py-2 pr-4 font-semibold text-zinc-500 uppercase tracking-widest">Step</th>
                      <th className="py-2 pr-4 font-semibold text-zinc-500 uppercase tracking-widest">Session</th>
                      <th className="py-2 pr-4 font-semibold text-zinc-500 uppercase tracking-widest">User</th>
                      <th className="py-2 pr-4 font-semibold text-zinc-500 uppercase tracking-widest">Template</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((e, i) => (
                      <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="py-2 pr-4 font-mono text-zinc-600 whitespace-nowrap">{e.ts ? e.ts.slice(0, 19).replace("T", " ") : "—"}</td>
                        <td className="py-2 pr-4">
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold ${e.event === "download" ? "bg-green-50 text-green-700" : e.event === "step_advance" ? "bg-blue-50 text-blue-700" : "bg-zinc-100 text-zinc-700"}`}>
                            {e.event}
                          </span>
                        </td>
                        <td className="py-2 pr-4 font-mono text-zinc-700">{e.step || "—"}</td>
                        <td className="py-2 pr-4 font-mono text-zinc-500">{e.session_id || "—"}</td>
                        <td className="py-2 pr-4 font-mono text-zinc-500">{e.user_id ? e.user_id.slice(0, 12) : "anon"}</td>
                        <td className="py-2 pr-4 font-mono text-zinc-700">{e.template || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
        <Footer />
      </main>
    </>
  );
}
